import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Plug, TriangleAlert } from "lucide-react";

import { AssistantCard, AssistantCardBody, AssistantCardFooter } from "@/agent/theme/AssistantCard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  agentOAuthService,
  consentErrorMessage,
  ConsentDetails,
  OPT_IN_SCOPES,
} from "@/services/agentOAuth";

const EXPIRED = "This sign-in link no longer works. Start again from the app.";

/**
 * An MCP app (claude.ai, ChatGPT) asks to use the assistant as the signed-in user. The user picks
 * what it may do and is sent back to the app. It never renders inside a frame (clickjacking).
 */
const OAuthConsent: React.FC = () => {
  const { request = "" } = useParams<{ request: string }>();
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const framed = window.top !== window.self;

  useEffect(() => {
    if (framed) return;
    agentOAuthService
      .getConsent(request)
      .then((data) => {
        setDetails(data);
        setPicked(
          new Set(data.scopes.map((s) => s.id).filter((id) => !OPT_IN_SCOPES.includes(id)))
        );
      })
      .catch((e) => setError(consentErrorMessage(e, EXPIRED)));
  }, [request, framed]);

  if (framed) return null;

  const decide = async (decision: "allow" | "deny") => {
    // aria-disabled, not disabled, while it goes through: focus stays on the pressed button.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await agentOAuthService.decide(request, decision, [...picked]));
    } catch (e) {
      setError(consentErrorMessage(e, "That didn't go through. Try again."));
      setBusy(false);
    }
  };

  const toggle = (id: string, on: boolean) =>
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const name = details?.client_name || "An app";

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 max-sm:items-start max-sm:p-0">
      {/* The assistant radius as an arbitrary value: tailwind-merge does not know rounded-assistant,
          so it would keep Card's rounded-xl, which wins on CSS order. */}
      <AssistantCard className="w-full max-w-md rounded-[var(--assistant-radius)] max-sm:max-w-none max-sm:rounded-none max-sm:border-x-0">
        {!details ? (
          error ? (
            <p role="alert" className="px-3.5 py-3 text-xs text-destructive">
              {error}
            </p>
          ) : (
            <div className="flex justify-center p-8">
              <Loader2
                className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
                aria-label="Loading"
              />
            </div>
          )
        ) : (
          <>
            {/* AssistantCardHeader's title truncates; this one wraps, so the name and host show in full. */}
            <div className="flex items-start gap-2 px-3.5 pb-2 pt-3">
              <Plug className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <h1 className="min-w-0 break-words text-sm font-semibold leading-snug tracking-tight">
                {/* <bdi> isolates the self-asserted name and the host, so neither reorders the other. */}
                Connect <bdi>{name}</bdi> to Steddy
                {details.redirect_host && (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    · <bdi>{details.redirect_host}</bdi>
                  </span>
                )}
              </h1>
            </div>
            <AssistantCardBody>
              <ul className="space-y-1">
                {details.scopes.map((scope) => (
                  <li key={scope.id}>
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-1 py-1.5 max-sm:min-h-10 max-sm:py-2.5 sm:hover:bg-muted/60">
                      <Checkbox
                        className="mt-0.5 focus-visible:ring-2"
                        checked={picked.has(scope.id)}
                        onCheckedChange={(checked) => toggle(scope.id, checked === true)}
                        disabled={busy}
                      />
                      <span className="min-w-0">
                        <span className="block">{scope.label}</span>
                        {scope.warning && (
                          <span className="mt-0.5 flex items-start gap-1 text-xs text-warning">
                            <TriangleAlert className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                            {scope.warning}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </AssistantCardBody>
            {error && (
              <p role="alert" className="px-3.5 pb-2 text-xs text-destructive">
                {error}
              </p>
            )}
            <AssistantCardFooter>
              <Button
                className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50 max-sm:h-10"
                onClick={() => decide("allow")}
                disabled={picked.size === 0}
                aria-disabled={busy}
              >
                {busy && (
                  <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
                )}
                Allow
              </Button>
              <Button
                className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50 max-sm:h-10"
                variant="outline"
                onClick={() => decide("deny")}
                aria-disabled={busy}
              >
                Cancel
              </Button>
            </AssistantCardFooter>
            <span role="status" className="sr-only">
              {busy ? "Connecting" : ""}
            </span>
          </>
        )}
      </AssistantCard>
    </div>
  );
};

export default OAuthConsent;
